import { AsyncPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  EntityMatchDecisionTypeEntity,
  EntityMatchDecisionTypeKey,
  ScreeningCaseCompositeEntity,
  ScreeningCaseReviewCompositeEntity,
  ScreeningCaseReviewStatusEntity,
} from '@nk/data-entities-lib';
import { ButtonDirective } from '@nk/ui-lib';
import { DateFormats, FormControls } from '@nk/utils-lib';
import { debounceTime, startWith, withLatestFrom } from 'rxjs/operators';
import { EntityMatchDecisionTypeTitleComponent } from '../../components/entity-match-decision-type-title/entity-match-decision-type-title.component';
import { ScreeningCaseReviewEditorService } from '../../services/screening-case-review-editor.service';
import { ScreeningCaseService } from '../../services/screening-case.service';
import {
  ENTITY_MATCH_DECISION_TYPE_TRANSLATIONS,
  ScreeningCaseReviewFormValue,
} from '../../utils/screening-case-review.utils';

@Component({
  selector: 'nk-screening-case-review-form',
  imports: [
    AsyncPipe,
    DatePipe,
    ReactiveFormsModule,
    TranslateModule,
    EntityMatchDecisionTypeTitleComponent,
    ButtonDirective,
  ],
  templateUrl: './screening-case-review-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningCaseReviewFormComponent implements OnInit {
  // Required inputs
  public screeningCaseComposite = input.required<ScreeningCaseCompositeEntity>();
  public severalMatches = input.required<boolean>();

  // Optional inputs
  public screeningCaseReviewCompositeEntity = input<ScreeningCaseReviewCompositeEntity | null>(null);
  public disabled = input(false);

  // Action-related inputs
  public screeningConfigUuid = input<string | null>(null);
  public screeningCaseReviewStatusList = input<ScreeningCaseReviewStatusEntity[]>([]);
  public showActions = input(false);
  public isReviewComplete = input(false);
  public reviewerEmail = input<string | null>(null);

  // Outputs
  public valueChanged = output<ScreeningCaseReviewFormValue>();
  public formValidityChanged = output<boolean>();
  public reviewSaved = output<void>();
  public reviewCreated = output<void>();

  // Services
  private readonly _destroy = inject(DestroyRef);
  private readonly _screeningCaseService = inject(ScreeningCaseService);
  private readonly _screeningCaseReviewEditorService = inject(ScreeningCaseReviewEditorService);

  // Public properties
  public entityMatchDecisionTypeList$ = this._screeningCaseService.entityMatchDecisionTypeListCache.get();
  private readonly _entityMatchDecisionTypeList = toSignal(this.entityMatchDecisionTypeList$);
  public form = new FormGroup<FormControls<ScreeningCaseReviewFormValue>>({
    entity_match_decision_type_uuid: new FormControl(null, [Validators.required]),
    pep_confirmed_flag: new FormControl<boolean>(false),
    screening_case_review_comment: new FormControl<string>(''),
  });
  public entityMatchDecisionTypeTranslations = ENTITY_MATCH_DECISION_TYPE_TRANSLATIONS;
  public showPepConfirmedFlag = signal(false);
  public isLoading = signal(false);
  public dateFormats = DateFormats;
  public uniqueId = signal<string>('');

  constructor() {
    effect(() => {
      // Set unique ID based on screening case UUID
      const caseComposite = this.screeningCaseComposite();
      if (caseComposite?.screening_case?.uuid) {
        this.uniqueId.set(caseComposite.screening_case.uuid);
      }

      const value = this.screeningCaseReviewCompositeEntity();
      const entityMatchDecisionTypeList = this._entityMatchDecisionTypeList();
      if (value && value.screening_case_review) {
        this.form.patchValue(value.screening_case_review);
        // Update showPepConfirmedFlag immediately when form is patched
        if (entityMatchDecisionTypeList) {
          const decisionTypeUuid = value.screening_case_review.entity_match_decision_type_uuid;
          const goodMatchType = entityMatchDecisionTypeList.find(
            (i) => i.entity_match_decision_type_key === EntityMatchDecisionTypeKey.good_match,
          );
          this.showPepConfirmedFlag.set(decisionTypeUuid === goodMatchType?.uuid);
        }
      }
      const disabled = this.disabled();
      if (disabled) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    });
  }

  public ngOnInit(): void {
    this.form.statusChanges.pipe(startWith(''), takeUntilDestroyed(this._destroy)).subscribe(() => {
      this.formValidityChanged.emit(this.form.status === 'VALID');
    });

    this.form.valueChanges
      .pipe(withLatestFrom(this.entityMatchDecisionTypeList$), debounceTime(10), takeUntilDestroyed(this._destroy))
      .subscribe(([data, entityMatchDecisionTypeList]) => {
        const decisionTypeUuid = data.entity_match_decision_type_uuid;
        const result = entityMatchDecisionTypeList.find(
          (i) => i.entity_match_decision_type_key === EntityMatchDecisionTypeKey.good_match,
        );
        this.showPepConfirmedFlag.set(decisionTypeUuid === result?.uuid);
        this.valueChanged.emit(data as ScreeningCaseReviewFormValue);
      });
  }

  public updatePepConfirmedFlag(entityMatchDecisionTypeList: EntityMatchDecisionTypeEntity[]): void {
    if (!this.screeningCaseComposite()?.screening_case?.pep_flag) {
      return;
    }
    const decisionTypeUuid = this.form.controls.entity_match_decision_type_uuid.value;
    const result = entityMatchDecisionTypeList.find(
      (i) => i.entity_match_decision_type_key === EntityMatchDecisionTypeKey.good_match,
    );
    this.form.controls.pep_confirmed_flag.setValue(false);
    if (decisionTypeUuid === result?.uuid) {
      this.form.controls.pep_confirmed_flag.setValidators([Validators.required]);
    } else {
      this.form.controls.pep_confirmed_flag.setErrors(null);
      this.form.controls.pep_confirmed_flag.setValidators([]);
    }
    this.form.controls.pep_confirmed_flag.updateValueAndValidity();
  }

  public isControlRequired(key: keyof ScreeningCaseReviewFormValue): boolean {
    return Boolean(this.form.controls[key].validator?.length);
  }

  public saveReview(commitReview = false): void {
    const screeningConfigUuid = this.screeningConfigUuid();
    const reviewComposite = this.screeningCaseReviewCompositeEntity();
    const statusList = this.screeningCaseReviewStatusList();

    if (!screeningConfigUuid || !reviewComposite || statusList.length === 0) {
      return;
    }

    this.isLoading.set(true);
    const formValue = this.form.value as ScreeningCaseReviewFormValue;

    this._screeningCaseReviewEditorService
      .saveReview(screeningConfigUuid, reviewComposite, statusList, formValue, commitReview)
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe({
        next: () => {
          this.isLoading.set(false);
          this.reviewSaved.emit();
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  public createReview(): void {
    const screeningConfigUuid = this.screeningConfigUuid();
    const caseComposite = this.screeningCaseComposite();
    const statusList = this.screeningCaseReviewStatusList();

    if (!screeningConfigUuid || statusList.length === 0) {
      return;
    }

    this.isLoading.set(true);

    this._screeningCaseReviewEditorService
      .createReview(screeningConfigUuid, caseComposite, statusList)
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe({
        next: () => {
          this.isLoading.set(false);
          this.reviewCreated.emit();
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }
}
